import React, { useEffect } from 'react';
import { Button } from './ui/button';
import { X } from 'lucide-react';
import DarkModeToggle from './ui/dark-mode-toggle';
import { useDarkMode } from './DarkModeContext';
import ProfilePicture from './ProfilePicture';

const PrivacyPolicyPage = ({ onClose, onSignIn, user, onShowPricing, onShowTerms, onShowCookiePreferences, onShowUpdates, mockupCount, onLogout }) => {
  const { isDarkMode } = useDarkMode();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-surface-1/80 backdrop-blur-md flex-shrink-0 sticky top-0 z-50">
        <div className="w-full pl-6 pr-4 sm:pl-6 sm:pr-6 lg:pl-6 lg:pr-6">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center">
              <img 
                src={isDarkMode ? "/RandomMockups-Logo-LightMode.png" : "/RandomMockups-Logo.png"} 
                alt="RandomMockups Logo" 
                className="mr-1.5"
                style={{ height: '40px', width: 'auto' }}
              />
              <h1 
                className="text-xl font-semibold text-foreground cursor-pointer hover:text-muted-foreground transition-colors"
                onClick={onClose}
              >
                RandomMockups
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <DarkModeToggle />
              {!user && (
                <Button 
                  onClick={onSignIn}
                  variant="outline"
                  size="sm"
                  className="text-foreground hover:text-foreground bg-surface-2 hover:bg-muted"
                >
                  Sign in
                </Button>
              )}
              {user && (
                <button
                  onClick={() => {
                    onShowPricing();
                    window.history.pushState({}, '', '/pricing');
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:bg-muted transition-colors p-2 rounded-lg"
                >
                  <ProfilePicture user={user} className="w-6 h-6 rounded-full" />
                  <span className={`bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center ${
                    mockupCount >= 10 
                      ? 'rounded-full px-2 py-1 min-w-8 h-6' 
                      : 'rounded-full w-5 h-5'
                  }`}>
                    {mockupCount}
                  </span>
                </button>
              )}
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[900px] mx-auto px-6 py-8 flex-1">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-muted-foreground">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
            <p className="text-foreground mb-4">
              RandomMockups ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service. By using RandomMockups, you consent to the data practices described in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">2. Information We Collect</h2>
            <div className="text-foreground space-y-3">
              <p>
                <strong>2.1 Account Information:</strong> When you sign in using a third-party authentication provider (e.g., Google), we collect:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Email address</li>
                <li>Display name</li>
                <li>Profile picture (if available)</li>
                <li>User ID from the authentication provider</li>
              </ul>
              <p>
                <strong>2.2 Uploaded Content:</strong> We collect and store images you upload to our service for the purpose of generating mockups. This content is stored securely and is only accessible to you and our service systems.
              </p>
              <p>
                <strong>2.3 Usage Data:</strong> We automatically collect information about how you use our service, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Number of mockups generated</li>
                <li>Subscription status and payment history</li>
                <li>Device and browser information</li>
                <li>IP address and location data (general geographic location)</li>
                <li>Timestamps of your activities</li>
              </ul>
              <p>
                <strong>2.4 Payment Information:</strong> Payment processing is handled by Stripe, a third-party payment processor. We do not store your full credit card information. Stripe may collect payment card details, billing address, and other payment-related information as necessary to process transactions.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">3. How We Use Your Information</h2>
            <div className="text-foreground space-y-3">
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, maintain, and improve our service</li>
                <li>Process your mockup generation requests</li>
                <li>Manage your account and track your credit usage</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send you service-related communications (e.g., account updates, payment confirmations)</li>
                <li>Respond to your feedback and support requests</li>
                <li>Analyze usage patterns to improve our service</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">4. Third-Party Services</h2>
            <div className="text-foreground space-y-3">
              <p>We use the following third-party services that may collect and process your information:</p>
              <p>
                <strong>4.1 Firebase (Google):</strong> We use Firebase for authentication, data storage (Firestore), and file storage. Firebase may collect and process your data according to Google's Privacy Policy. Information stored includes user accounts, mockup metadata, and uploaded images.
              </p>
              <p>
                <strong>4.2 Stripe:</strong> We use Stripe to process payments. Stripe collects payment information and transaction data. For more information, see Stripe's Privacy Policy.
              </p>
              <p>
                <strong>4.3 Google Gemini:</strong> We use Google Gemini AI to generate mockups. Images you upload are sent to Gemini for processing. This is necessary to provide the core functionality of our service. Google's use of data is governed by their Privacy Policy.
              </p>
              <p>
                <strong>4.4 Google Analytics:</strong> We use Google Analytics to understand how visitors interact with our website. This service uses cookies and similar technologies to collect information about your browsing behavior. You can control cookie preferences through our Cookie Settings.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">5. Cookies and Tracking Technologies</h2>
            <div className="text-foreground space-y-3">
              <p>
                We use cookies and similar tracking technologies to track activity on our service and store certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.
              </p>
              <p>
                <strong>5.1 Types of Cookies:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for the service to function properly. These cannot be disabled.</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website. You can control these through Cookie Settings.</li>
              </ul>
              <p>
                <strong>5.2 Cookie Preferences:</strong> You can manage your cookie preferences at any time through the Cookie Settings link in our footer. Note that disabling certain cookies may affect the functionality of our service.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">6. Data Storage and Security</h2>
            <div className="text-foreground space-y-3">
              <p>
                <strong>6.1 Data Storage:</strong> Your data is stored on secure servers provided by Firebase (Google Cloud Platform). We implement appropriate technical and organizational measures to protect your personal information.
              </p>
              <p>
                <strong>6.2 Security Measures:</strong> We use industry-standard security measures, including encryption in transit and at rest, secure authentication, and access controls. However, no method of transmission over the internet or electronic storage is 100% secure.
              </p>
              <p>
                <strong>6.3 Data Location:</strong> Your data may be stored and processed in the United States or other countries where our service providers maintain facilities. By using our service, you consent to the transfer of your information to these locations.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">7. Data Retention</h2>
            <div className="text-foreground space-y-3">
              <p>
                <strong>7.1 Account Data:</strong> We retain your account information for as long as your account is active or as needed to provide you with our service. If you delete your account, we will delete or anonymize your personal information, except where we are required to retain it for legal or legitimate business purposes.
              </p>
              <p>
                <strong>7.2 Uploaded Content:</strong> Images you upload are retained for as long as your account is active. You can delete your uploaded content at any time through the service interface. Deleted content will be permanently removed from our systems within a reasonable timeframe.
              </p>
              <p>
                <strong>7.3 Payment Records:</strong> We retain payment and transaction records as required by law and for accounting purposes, typically for a minimum of 7 years.
              </p>
              <p>
                <strong>7.4 Analytics Data:</strong> Aggregated and anonymized analytics data may be retained indefinitely for business intelligence purposes.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">8. Your Rights and Choices</h2>
            <div className="text-foreground space-y-3">
              <p>Depending on your location, you may have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
                <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Objection:</strong> Object to certain processing of your personal information</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
              <p>
                To exercise these rights, please contact us through the feedback form in the application or via email. We will respond to your request within a reasonable timeframe.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">9. Children's Privacy</h2>
            <p className="text-foreground mb-4">
              Our service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately, and we will take steps to delete such information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-foreground mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any significant changes by updating the "Last updated" date at the top of this page. Your continued use of the service after such modifications constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">11. Contact Us</h2>
            <p className="text-foreground mb-4">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us through the feedback form in the application or via email.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-surface-1 py-8">
        <div className="max-w-[1480px] mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground space-y-2 md:space-y-0">
            <div>
              All rights reserved
            </div>
            <div className="flex space-x-4">
              <button 
                onClick={() => {
                  onShowUpdates();
                  window.history.pushState({}, '', '/updates');
                }}
                className="hover:text-foreground transition-colors"
              >
                Updates
              </button>
              <button 
                onClick={() => {
                  onShowPricing();
                  window.history.pushState({}, '', '/pricing');
                }}
                className="hover:text-foreground transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => {
                  onShowTerms();
                  window.history.pushState({}, '', '/terms');
                }}
                className="hover:text-foreground transition-colors"
              >
                Terms
              </button>
              <button 
                onClick={() => {
                  // Already on Privacy page, no action needed
                }}
                className="hover:text-foreground transition-colors font-medium"
              >
                Privacy
              </button>
              <button 
                onClick={onShowCookiePreferences}
                className="hover:text-foreground transition-colors"
              >
                Cookie Settings
              </button>
            </div>
            <div>
              Made with 🍌 by <a href="https://x.com/txh" target="_blank" rel="noopener noreferrer" className="font-medium hover:text-foreground transition-colors">@txh</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;

